import "../../../shared/betslip/betslip.css";
import {LongtermBetslip, useLongtermBetslip, useLongtermSlipOptions} from "../../../features/betslip/longterm.service";
import {LongtermSelectionRow} from "./longterm-selection";
import {AcceptOddsChange} from "../components/accept-odds-change";
import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {CurrencyFormat, preventBetSlipValues} from "../../../services/misc.functions";
import arrowDown from "../../../assets/feather/arrow-down-s-line.svg";
import arrowUp from "../../../assets/feather/arrow-up-s-line.svg";
import infoIcon from "../../../assets/feather/info.svg";
import _ from "lodash";
import {auth} from "../../../store/auth.store";
import {modalServiceSubject} from "../../../services/modal.service";
import paths from "../../../paths";
import {isMobile} from "../../../App";
import {BettingTypeIds, BettingTypesMap} from "../../../features/core/definitions";
import {captureCouponImage, CouponImage, resolveCouponImage} from "../coupon-image";

// Uzun vadelinin bahis türü SABİT (1889): sayfada tür/alt tür seçimi yok, tutar ve
// kupon sınırları doğrudan katalogdan okunuyor.
const longtermBettingType: any = BettingTypesMap[BettingTypeIds.longterm];

// ===== UZUN VADELİ KUPON PANELİ: hazırlık =====

/** Bahis adedi ve kupon tutarı, uzun vadelinin KENDİ servisinden. */
function resolveBetslipTotals(): { betCount: any; totalAmount: number } {
    const betCount = (LongtermBetslip.getSelection() ? 1 : 0);
    const multiply = Number(LongtermBetslip.getOptions().multiply);
    return {betCount, totalAmount: Number((multiply * betCount * longtermBettingType.price).toFixed(2))};
}

interface BetslipFrame {
    ready: boolean;
    pathname: string;
    navigate: any;
    scheduleErrorClear: () => void;
    hasStaleItem: boolean;
    runsMap: any;
    runsList: any;
    tabs: any[];
    runs: any[];
    emptyTabs: any[];
    maxHorseSize: number;
    betCount: any;
    totalAmount: number;
    betSlipMaxMultiply: number;
    multiplyChoices: number[];
}

export function useBetslipFrame(): BetslipFrame {
    const navigate = useNavigate();
    const {pathname} = useLocation();
    useLongtermBetslip();
    const options = useLongtermSlipOptions();

    const errorClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
    }, []);
    // Kapanmış koşu / oynanamaz seçim: OYNA kilitli, onayda ikinci kez bakılıyor.
    const hasStaleItem = !!LongtermBetslip.getSelection() && LongtermBetslip.getSelection()?.hippodrome?.status === false;

    const scheduleErrorClear = () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
        errorClearTimer.current = setTimeout(() => LongtermBetslip.setOptions({errorMessage: null}), 5000);
    };

    // Uzun vadelide ayak/koşu tablosu yok; koşu haritaları hiç kurulmuyor.
    const runsMap: any = {};
    const runsList: any = [];
    const tabs: any[] = [];
    const runs: any[] = [];
    const emptyTabs: any[] = [];
    const maxHorseSize = 0;

    const {betCount, totalAmount: computedTotal} = resolveBetslipTotals();
    let totalAmount = computedTotal;

    const maxMsg = 'Toplam bahis tutarı ' + CurrencyFormat(longtermBettingType.maxCouponAmount) + ' TL\'yi geçemez.';
    const overMax = totalAmount > longtermBettingType.maxCouponAmount;
    useEffect(() => {
        if (overMax) {
            if (options.errorMessage !== maxMsg) {
                LongtermBetslip.setOptions({errorMessage: maxMsg});
            }
        } else if (options.errorMessage === maxMsg) {
            LongtermBetslip.setOptions({errorMessage: null});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overMax, maxMsg]);
    const betSlipMaxMultiply = betCount > 0
        ? Math.floor(longtermBettingType.maxCouponAmount / betCount)
        : longtermBettingType.maxCouponAmount;

    let multiplyChoices: any = [];
    if (betSlipMaxMultiply > 200) {
        multiplyChoices = _.range(1, 101);
        const mod = betSlipMaxMultiply % 50;
        multiplyChoices.push(..._.range(200, betSlipMaxMultiply, 50));
        if (mod > 0) {
            multiplyChoices.push(multiplyChoices[multiplyChoices.length - 1] + mod);
        }
    } else {
        multiplyChoices = _.range(1, betSlipMaxMultiply + 1);
    }

    if (overMax) {
        totalAmount = options.multiply * betCount * longtermBettingType.price;
    }

    return {
        ready: true, pathname, navigate, scheduleErrorClear, hasStaleItem,
        runsMap, runsList, tabs, runs, emptyTabs, maxHorseSize,
        betCount, totalAmount, betSlipMaxMultiply, multiplyChoices,
    };
}


type AfterSaveButton = { label: string; action: () => void };

type PlayCouponContext = {
    navigate: (to: string) => void;
    totalAmount: any;
    afterSave: (bulletinUrl: string) => AfterSaveButton;
};

const PROCESSING_MODAL = {
    case: 'info',
    title: 'Kuponunuz Oynanıyor',
    innerHtml: true,
    content: '<div class="text-center"><div class="spinner-border text-primary mb-2" role="status"></div><p>Kuponunuz oynanmak üzere iletildi. Yanıt gelene kadar lütfen bekleyiniz.</p></div>',
    confirm: null,
    backdropCloseDisabled: true,
};

async function playCouponFlow(save: boolean, ctx: PlayCouponContext, couponImage?: CouponImage) {
    const {navigate, afterSave} = ctx;
    // Servis aynı korumayı taşıyor (`submit` null döner); burada erken çıkmak bekleme
    // modalını hiç açmamak için.
    if (LongtermBetslip.getOptions().inProgress || !LongtermBetslip.getSelection()) return;

    if (!auth.member || !auth.member.id) {
        modalServiceSubject.next({
            title: 'Üye Girişi',
            case: 'info',
            content: `Bahis yapabilmeniz için önce oturum açmanız gerekli. Üye değilseniz hemen üye olabilir yada giriş yaparak bahis yapabilirsiniz.`,
            confirm: {
                sure: {
                    label: 'Giriş Yap',
                    class: 'btn btn-success',
                    action: () => {
                        navigate(paths.login.url)
                    }
                },
                cancel: {
                    label: 'Üye Ol',
                    class: 'btn btn-primary',
                    action: () => {
                        navigate(paths.register.url)
                    }
                }
            }
        })
        return;
    }

    // Uzun vadelide ayak/koşu yok: kapanma ölçütü seçimin ait olduğu yarışın kendi durumu.
    const selectionForSubmit: any = LongtermBetslip.getSelection();
    if (selectionForSubmit && selectionForSubmit.hippodrome?.status === false) {
        modalServiceSubject.next({
            title: 'Kupon Güncel Değil',
            case: 'warning',
            content: `Kuponunuzdaki ${selectionForSubmit.hippodrome?.hippodrome} yarışı için bahisler kapandı. Bu seçimi kaldırıp kuponunuzu yeniden oluşturmanız gerekiyor.`,
            confirm: {
                cancel: {
                    label: 'Tamam',
                }
            }
        });
        return;
    }

    const capture = couponImage ?? captureCouponImage();
    const processingModalTimeout = setTimeout(() => modalServiceSubject.next(PROCESSING_MODAL), 100);
    const result = await LongtermBetslip.submit(save, await resolveCouponImage(capture));
    clearTimeout(processingModalTimeout);

    // Servis `inProgress` korumasından ya da boş kupondan null döndüyse: sessiz kalmak yerine
    // açık kalmış olabilecek bekleme modalı kapatılıyor.
    if (!result) {
        setTimeout(() => modalServiceSubject.next(false), 1500);
        return;
    }

    if (result.status === true && save === false) {
        LongtermBetslip.clear();

        if (result.data.coupon_data.approve_code != null) {
            modalServiceSubject.next({
                case: 'success',
                title: 'Kuponunuz Oynandı',
                innerHtml: true,
                content: `<p class="text-center">
<b>${result.data.coupon_data.approve_code}</b> kodu ile <b>${CurrencyFormat(Number(result.data.coupon_data.amount || 0))} ₺</b> tutarındaki <b>${BettingTypesMap[Number(result.data.coupon_data.bet_type)]?.name}</b> kuponunuz başarıyla oynandı. <br/>Kuponunuzun durumunu kuponlarım sayfasından takip edebilirsiniz.</p>`,
                confirm: {
                    sure: {
                        label: 'At Yarışı Kuponlarım',
                        action: () => {
                            navigate(paths.memberTickets.url);
                        }
                    },
                    cancel: {
                        label: "Bülten'e Dön",
                        action: () => {
                            if (isMobile) {
                                navigate(paths.bettingLongterm.url);
                            }
                        }
                    }
                }
            })
        } else {
            modalServiceSubject.next({
                case: 'info',
                title: 'Kupon Durumu',
                innerHtml: true,
                content: `<p class="text-center">Kuponunuz sisteme iletildi fakat henüz oynanma durumu cevabı alınamadı. <br/>Kuponunuzun durumunu kuponlarım sayfasından takip edebilirsiniz.</p>`,
                confirm: {
                    sure: {
                        label: 'At Yarışı Kuponlarım',
                        action: () => {
                            navigate(paths.memberTickets.url);
                        }
                    },
                    cancel: {
                        label: "Bülten'e Dön",
                        action: () => {
                            if (isMobile) {
                                navigate(paths.bettingLongterm.url);
                            }
                        }
                    }
                }
            })
        }
    } else if (result.status && save === true) {
        modalServiceSubject.next({
            case: 'success',
            title: 'Kuponunuz Kaydedildi',
            content: "Kuponunuz başarıyla Kaydedildi. Kuponunuzu kuponlarım sayfasında görüntüleyebilirsiniz",
            confirm: {
                sure: {
                    label: 'Kayıtlı Kuponlarım',
                    action: () => {
                        navigate(paths.memberSavedTickets.url);
                    }
                },
                cancel: afterSave(paths.bettingLongterm.url)
            }
        })
    } else {
        let error_description: any = null;
        let error_message: any = null;
        if (result.data && result.data.error) {
            error_description = result.data.error.error_description;
            error_message = result.data.error.error_message;
        }

        let title: any = 'Hata';
        let sure: any = null;
        if (typeof (result.message) === 'string' && result.message.indexOf('Oturum') !== -1) {
            sure = {
                label: 'Giriş Yap',
                action: () => {
                    navigate(paths.login.url);
                }
            }
        }
        if (error_message === 'TIMEOUT') {
            title = 'Dikkat';
            sure = {
                label: 'At Yarışı Kuponlarım',
                action: () => {
                    navigate(paths.memberTickets.url);
                }
            }
        }

        modalServiceSubject.next({
            case: 'danger',
            title: title,
            content: error_description || (typeof (result.message) === 'object' ? JSON.stringify(result.message) : result.message),
            confirm: {
                sure: sure,
                cancel: {
                    label: 'Tamam',
                }
            }
        })
    }
}



// OYNA öncesi doğrulama - kenar çubuğu (onay modalından önce) ve /bilet-onay sayfası (sayfanın
// kendisi onay) aynı kuralları uyguluyor.
function validateLongtermCoupon(betCount: number, totalAmount: number): string | null {
    const options = LongtermBetslip.getOptions();
    const selection: any = LongtermBetslip.getSelection();
    if (betCount === 0 || !selection) {
        return 'Kuponunuz da oynanacak bir yarış bulunmamaktadır.';
    }
    if (selection.hippodrome?.status === false) {
        return 'Kuponunuzdaki yarış için bahisler kapandı, lütfen seçimi kaldırın.';
    }
    // Oranlar gönderim anında değişebiliyor; kullanıcı bunu kabul etmeden kupon oynatılmıyor.
    if (!options.acceptChangedOdds) {
        return 'Bileti oynayabilmek için oranların değişebileceğini kabul etmelisiniz.';
    }
    if (totalAmount > 12000) {
        return 'Kupon tutarı 12000 TL\'den fazla olamaz.';
    }
    if (options.shareCoupon !== true && (options.usePointBalance ? Number(auth.member.balance_debit) + Number(auth.member.balance_point) : Number(auth.member.balance_debit)) < totalAmount) {
        return 'Bakiyeniz yetersiz.';
    }
    if (totalAmount < longtermBettingType.minCouponAmount) {
        return 'Minumum bahis tutarı ' + CurrencyFormat(longtermBettingType.minCouponAmount) + ' TL olmalıdır.';
    }
    return null;
}

// Oynatma düğmesinin kapısı: uzun vadelide seçimin ait olduğu yarışın açık olması yeterli.
// Seçim uzun vadelinin KENDİ servisinde durduğu için hipodrom da oradan okunuyor.
function canPlayNow(): boolean {
    const selection = LongtermBetslip.getSelection();
    return selection === null || !!selection.hippodrome?.status;
}

export function LongtermMobileBetslip(prop: any) {
    const [isMinimize, setIsMinimize] = useState<boolean>(false);
    const frame = useBetslipFrame();
    const {navigate, betCount, betSlipMaxMultiply, hasStaleItem} = frame;
    const totalAmount = frame.totalAmount;

    if (!frame.ready) {
        return <></>;
    }

    const playCoupon = (save: boolean = false, imgData?: CouponImage) => playCouponFlow(save, {
        navigate,
        totalAmount,
        afterSave: () => ({label: 'Yeni Kupon Oyna', action: () => {}}),
    }, imgData);



    return <>
        <div className={`betslip-mobile-coupon ${isMinimize ? 'close' : 'open'}`}>
            <div className={"minimize-btn"} onClick={() => {
                setIsMinimize(prevState => !prevState);
            }}>
                Bileti {isMinimize ? "Göster" : "Gizle"} <img src={isMinimize ? arrowUp : arrowDown}/>
            </div>

            <div className="betslip-mobile-coupon-wrapper">
                {LongtermBetslip.getOptions().errorMessage && <div className="betslip-mobile-coupon-error-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 12 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd"
                              d="M6 0.5C2.96243 0.5 0.5 2.96243 0.5 6C0.5 9.03757 2.96243 11.5 6 11.5C9.03757 11.5 11.5 9.03757 11.5 6C11.5 2.96243 9.03757 0.5 6 0.5ZM6 3.5C5.72386 3.5 5.5 3.72386 5.5 4C5.5 4.27614 5.72386 4.5 6 4.5H6.005C6.28114 4.5 6.505 4.27614 6.505 4C6.505 3.72386 6.28114 3.5 6.005 3.5H6ZM6.5 6C6.5 5.72386 6.27614 5.5 6 5.5C5.72386 5.5 5.5 5.72386 5.5 6V8C5.5 8.27614 5.72386 8.5 6 8.5C6.27614 8.5 6.5 8.27614 6.5 8V6Z"
                              fill="#FFE700"></path>
                    </svg>
                    <p className="text-xsmall text-white ">{LongtermBetslip.getOptions().errorMessage}</p>
                </div>}

                <div className="betslip-mobile-balance-wrapper">
                    <div className="betslip-mobile-debit-balance">
                        <span className="text-xxsmall text-white">Bakiye</span>
                        <span
                            className="text-xxsmall text-white text-bold">{auth.member ? auth.member.balance_debit : '-'}₺</span>
                    </div>



                    <div className="betslip-mobile-point-balance">
                        <span className="text-xxsmall text-white d-flex align-items-center">Puan</span>
                        <span
                            className="text-xxsmall text-white text-bold">{auth.member ? auth.member.balance_point : '-'}₺</span>
                        {auth.member && auth.authentication && <button onClick={() => {
                            LongtermBetslip.setOptions({usePointBalance: !LongtermBetslip.getOptions().usePointBalance})
                        }}
                                                                       className={`button xs primary filled betting-mobile-point-checkbox ${LongtermBetslip.getOptions().usePointBalance ? 'point-selected' : ''}`}>
                            {LongtermBetslip.getOptions().usePointBalance &&
                                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8"
                                     fill="none">
                                    <path d="M6.66658 2L2.99992 5.66667L1.33325 4" stroke="white" strokeWidth="1.6"
                                          strokeLinecap="round" strokeLinejoin="round"></path>
                                </svg>}
                        </button>}
                    </div>
                </div>
                <div className="betslip-mobile-action-wrapper">
                    <button className="button primary filled action-delete" onClick={() => {
                        LongtermBetslip.clear();
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18"
                             fill="none">
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M8.37109 0.750001H9.62891C10.0242 0.749988 10.3654 0.749978 10.6463 0.77293C10.9428 0.797155 11.2377 0.850642 11.5215 0.995238C11.9448 1.21095 12.289 1.55516 12.5048 1.97852C12.6494 2.26231 12.7028 2.55722 12.7271 2.85373C12.7476 3.10556 12.7498 3.40573 12.75 3.75H15.75C16.1642 3.75 16.5 4.08579 16.5 4.5C16.5 4.91422 16.1642 5.25 15.75 5.25H15V12.931C15 13.5347 15 14.033 14.9669 14.4389C14.9324 14.8604 14.8585 15.248 14.673 15.612C14.3854 16.1765 13.9265 16.6354 13.362 16.923C12.998 17.1085 12.6104 17.1824 12.1889 17.2169C11.783 17.25 11.2847 17.25 10.681 17.25H7.31901C6.71529 17.25 6.21702 17.25 5.81113 17.2169C5.38956 17.1824 5.00203 17.1085 4.63803 16.923C4.07354 16.6354 3.6146 16.1765 3.32698 15.612C3.14151 15.248 3.06759 14.8604 3.03315 14.4389C2.99998 14.033 2.99999 13.5347 3 12.931L3 5.25H2.25C1.83579 5.25 1.5 4.91422 1.5 4.5C1.5 4.08579 1.83579 3.75 2.25 3.75H5.25002C5.25023 3.40573 5.25235 3.10556 5.27293 2.85373C5.29715 2.55722 5.35064 2.26231 5.49524 1.97852C5.71095 1.55516 6.05516 1.21095 6.47852 0.995238C6.76231 0.850642 7.05722 0.797155 7.35373 0.77293C7.63464 0.749978 7.97576 0.749988 8.37109 0.750001ZM7.5 7.875C7.91421 7.875 8.25 8.21079 8.25 8.625V12.375C8.25 12.7892 7.91421 13.125 7.5 13.125C7.08579 13.125 6.75 12.7892 6.75 12.375V8.625C6.75 8.21079 7.08579 7.875 7.5 7.875ZM11.25 8.625C11.25 8.21079 10.9142 7.875 10.5 7.875C10.0858 7.875 9.75 8.21079 9.75 8.625V12.375C9.75 12.7892 10.0858 13.125 10.5 13.125C10.9142 13.125 11.25 12.7892 11.25 12.375V8.625Z"
                                  fill="url(#paint0_linear_6725_145842)">
                            </path>
                            <defs>
                                <linearGradient id="paint0_linear_6725_145842" x1="9" y1="0.75" x2="9" y2="17.25"
                                                gradientUnits="userSpaceOnUse">
                                    <stop stopColor="white">
                                    </stop>
                                    <stop offset="1" stopColor="white" stopOpacity="0.7">
                                    </stop>
                                </linearGradient>
                            </defs>
                        </svg>
                    </button>
                    <button className="button tertiary filled action-save" onClick={() => {
                        playCoupon(true);
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18"
                             fill="none">
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M11.9039 1.56224C11.6426 1.4995 11.3735 1.49975 11.0684 1.50004L5.81903 1.50008C5.2153 1.50007 4.71703 1.50006 4.31113 1.53322C3.88956 1.56767 3.50203 1.64159 3.13803 1.82706C2.57354 2.11468 2.1146 2.57362 1.82698 3.1381C1.64151 3.50211 1.56759 3.88963 1.53315 4.31121C1.49998 4.7171 1.49999 5.21537 1.5 5.81909V12.1811C1.49999 12.7848 1.49998 13.2831 1.53315 13.6889C1.56759 14.1105 1.64151 14.498 1.82698 14.862C2.1146 15.4265 2.57354 15.8855 3.13803 16.1731C3.50203 16.3586 3.88956 16.4325 4.31113 16.4669C4.71702 16.5001 5.21529 16.5001 5.81901 16.5001H12.181C12.7847 16.5001 13.283 16.5001 13.6889 16.4669C14.1104 16.4325 14.498 16.3586 14.862 16.1731C15.4265 15.8855 15.8854 15.4265 16.173 14.862C16.3585 14.498 16.4324 14.1105 16.4669 13.6889C16.5 13.2831 16.5 12.7848 16.5 12.1811L16.5 6.93172C16.5003 6.62654 16.5006 6.35748 16.4378 6.09614C16.3827 5.86656 16.2918 5.64709 16.1684 5.44577C16.028 5.21662 15.8376 5.02654 15.6216 4.81095L13.1891 2.37848C12.9735 2.16249 12.7835 1.97206 12.5543 1.83164C12.353 1.70827 12.1335 1.61736 11.9039 1.56224ZM11.5538 3.02072C11.4875 3.00482 11.4085 3 11.0059 3H5.85C5.62662 3 5.42812 3.00007 5.25 3.00132V4.79992C5.25 5.21996 5.25 5.42998 5.33175 5.59042C5.40365 5.73154 5.51839 5.84627 5.65951 5.91818C5.81994 5.99992 6.02996 5.99992 6.45 5.99992H11.55C11.97 5.99992 12.1801 5.99992 12.3405 5.91818C12.4816 5.84627 12.5963 5.73154 12.6683 5.59042C12.75 5.42998 12.75 5.21996 12.75 4.79992V4.06066L12.1726 3.48327C11.8879 3.1986 11.8286 3.14611 11.7706 3.11052C11.7035 3.0694 11.6303 3.03909 11.5538 3.02072ZM6.45 9.74992H11.55C11.97 9.74992 12.1801 9.74992 12.3405 9.83167C12.4816 9.90357 12.5963 10.0183 12.6683 10.1594C12.75 10.3199 12.75 10.5299 12.75 10.9499V14.9999H5.25V10.9499C5.25 10.5299 5.25 10.3199 5.33175 10.1594C5.40365 10.0183 5.51839 9.90357 5.65951 9.83167C5.81994 9.74992 6.02996 9.74992 6.45 9.74992Z"
                                  fill="url(#paint0_linear_6725_145844)">
                            </path>
                            <defs>
                                <linearGradient id="paint0_linear_6725_145844" x1="9.00004" y1="1.5" x2="9.00004"
                                                y2="16.5001" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="white">
                                    </stop>
                                    <stop offset="1" stopColor="white" stopOpacity="0.7">
                                    </stop>
                                </linearGradient>
                            </defs>
                        </svg>
                    </button>
                    <div className="action-multipy-wrapper">
                            <span
                                className="text-xsmall text-white d-flex justify-content-center align-items-center w-100">Misli</span>
                        <input
                            type="number" className={"multipy-input hide-stepper"}
                            pattern="^[0-9\b]+$" min={20} max={betSlipMaxMultiply}
                            step={1} value={LongtermBetslip.getOptions().multiply || ''}
                            onKeyDown={preventBetSlipValues}
                            onChange={e => {
                                if (Number(e.target.value) <= 12000) {
                                    LongtermBetslip.setOptions({multiply: Number(e.target.value)})
                                } else {
                                    LongtermBetslip.setOptions({multiply: 12000})
                                }
                            }}
                            onBlur={() => {
                                // Uzun vadelide asgari misli 20.
                                if (!LongtermBetslip.getOptions().multiply || Number(LongtermBetslip.getOptions().multiply) < 20) {
                                    LongtermBetslip.setOptions({multiply: 20})
                                }
                            }}
                            onPaste={(e) => {
                                e.preventDefault()
                                return false;
                            }}
                        />
                    </div>
                    {canPlayNow() ? <button
                        className={`button tertiary outlined play-button ${LongtermBetslip.getOptions().errorMessage || betCount === 0 || hasStaleItem ? '' : 'play-active'}`}
                        disabled={LongtermBetslip.getOptions().errorMessage || betCount === 0 || hasStaleItem ? true : false}
                        onClick={() => {
                            navigate(paths.betslip.url, {state: {type: "longterm"}})
                        }}
                    >
                        {LongtermBetslip.getOptions().inProgress ? <>
                                <span className="text-small text-white text-bolder"
                                      style={{letterSpacing: "-0.3px"}}>OYNANIYOR</span>
                        </> : <>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>OYNA</span>
                            <span className="text-xxsmall text-white text-bold">
                                {CurrencyFormat(totalAmount)}₺</span></>}
                    </button> : <button
                        className={`button tertiary outlined play-button `}
                        disabled={true}>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>Bahisler Açılmadı</span>
                    </button>}
                </div>
            </div>
        </div>
    </>
}

export function LongtermBetslipSidebar(prop: any) {
    const [isOpen, setIsOpen] = useState<any>(false);
    const frame = useBetslipFrame();
    const {navigate, betCount, betSlipMaxMultiply, scheduleErrorClear, hasStaleItem} = frame;
    const totalAmount = frame.totalAmount;

    if (!frame.ready) {
        return <></>;
    }



    const longtermSelection = LongtermBetslip.getSelection();


    const playCoupon = (save: boolean = false, imgData?: CouponImage) => playCouponFlow(save, {
        navigate,
        totalAmount,
        afterSave: () => ({label: 'Yeni Kupon Oyna', action: () => {}}),
    }, imgData);

    const confirmCoupon = async () => {
        if (!(auth.authentication && auth.member && auth.member.id)) {
            navigate(`${paths.login.url}/${window.location.pathname.replaceAll('/', '|')}`);
            return;
        }
        const errorMessage = validateLongtermCoupon(betCount, totalAmount);
        if (errorMessage) {
            LongtermBetslip.setOptions({errorMessage});
            scheduleErrorClear();
            return;
        }
        const imgData = captureCouponImage();
        if (isMobile) {
            playCoupon(false, imgData);
        } else {
            showLongtermConfirmModal({totalAmount, playCoupon: (save: boolean = false) => playCoupon(save, imgData)});
        }
        scheduleErrorClear();
    }

    const showBettingTypeInfo = (bettingType: any) => {
        modalServiceSubject.next({
            case: 'info',
            title: bettingType.name,
            content: bettingType.info,
            confirm: {
                cancel: {
                    label: 'Tamam',
                    class: 'btn-warning fw-semibold w-100'
                }
            }
        })
    }

    const simpleView = (legs: any) => {
        let str: any = [];
        legs.forEach((l: any, i: number) => {
            if (i === 0) {
                str.push(l)
            } else if (Number(l) === Number(legs[i - 1]) + 1 && Number(l) === Number(legs[i + 1]) - 1) {
                if (str[str.length - 1] !== '-') {
                    str.push('-')
                }
            } else {
                str.push(l)
            }
        })
        return str.join(', ').replaceAll(', -, ', '-')
    }




    return <div id={"ekuri-betslip"} className={`ebetslip ${isOpen ? 'open' : 'closed'}`}>
        {
            !isOpen && <div id={"betslip-click-area"} onClick={() => {
                setIsOpen(true);
                if (isOpen) {

                    try {
                        var ecommerceValue = totalAmount;
                        const longtermSelection: any = LongtermBetslip.getSelection();
                        // @ts-ignore
                        dataLayer.push({
                            event: "view_cart",
                            ecommerce: {
                                currency: 'TRY',
                                value: ecommerceValue,
                                items: [
                                    {
                                        // Uzun vadelide ayak/koşu yok: olay alanları seçimin
                                        // kendi yarışından ve atından türetiliyor.
                                        item_id: `${longtermBettingType.name} / ${longtermSelection?.hippodrome?.hippodrome} / ${longtermSelection?.horse?.description}`,
                                        item_name: longtermBettingType.name,
                                        item_brand: longtermSelection?.hippodrome?.hippodrome,
                                        item_category: longtermSelection?.hippodrome?.hippodrome,
                                        item_category2: longtermSelection?.horse?.description,
                                        item_category3: longtermSelection?.hippodrome?.date,
                                    }
                                ]
                            }
                        })

                    } catch (e) {

                    }
                }

            }}>
                <div className="d-flex justify-content-center align-items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                         xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 10L8 6L4 10" stroke="var(--secondary-color)" strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                        </path>
                    </svg>
                </div>
                <div className="mini-coupon-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 18 14"
                         fill="none">
                        <path fillRule="evenodd" clipRule="evenodd"
                              d="M12.4967 2.03868C12.5259 2.06883 12.4673 2.09745 12.4673 2.09745C12.4673 2.09745 12.2636 2.15622 12.2049 2.24362C12.1463 2.30256 12.0598 2.50596 12.0598 2.50596C12.1763 2.47734 12.3222 2.44719 12.4673 2.38819C12.4967 2.38819 12.5259 2.41857 12.5553 2.44713C12.5839 2.44713 12.2049 2.5933 12.1184 2.65053C12.0598 2.71106 11.7688 2.94461 11.6816 2.94461C11.5945 2.94461 11.3898 2.97323 11.3898 2.97323C11.4192 3.032 11.4485 3.08924 11.5055 3.17663C11.5055 3.17663 11.3027 3.2944 11.3027 3.32295C11.2741 3.35311 11.1282 3.52811 11.1282 3.52811C11.1282 3.52811 11.6523 3.58518 11.8268 3.29439C11.6523 3.58518 11.2447 3.87774 10.3998 3.87774C9.52616 3.87774 8.82791 3.52817 8.36189 3.35316C7.86634 3.14976 7.22603 2.91444 6.70181 2.91444C6.20712 2.91444 5.62458 3.06061 5.24632 3.41193C4.57645 3.14976 3.64438 3.29433 3.20736 3.7029C2.79985 4.11146 2.39237 4.60895 2.33369 4.95715C2.24652 5.33862 2.0428 6.24297 1.75166 6.6533C1.34346 7.32228 0.877986 7.49881 0.732191 7.78961C0.586396 8.08216 0.586396 8.69566 0.614891 8.69566C0.673513 8.72428 0.878149 8.69566 0.994553 8.63671C1.22768 8.54932 1.86748 7.87859 2.10049 7.55748C2.33345 7.23477 3.1208 5.83298 3.23652 5.33858C3.35309 4.84263 3.46948 4.34514 3.81924 4.0828C4.19822 3.82045 4.66278 3.93663 4.66278 3.96679C4.66278 3.96679 4.22677 4.31653 4.19822 5.2798C4.16815 6.24292 4.75085 6.1269 4.45973 6.739C4.16809 7.26494 3.29532 6.91378 3.00438 7.23478C2.77125 7.49871 3.00438 8.43162 3.00438 8.6064C3.00438 8.78295 2.91563 9.59984 2.85859 9.805C2.82851 9.97977 2.85859 10.0958 2.82851 10.2136C2.79996 10.3025 3.00461 10.3296 3.00461 10.3296C3.00461 10.3296 2.82851 10.5616 2.74135 10.5059C2.65418 10.4758 2.59624 10.4758 2.5383 10.5059C2.47968 10.5059 2.36397 10.5616 2.24666 10.5059L1.78035 11.498C1.83914 11.5281 1.92631 11.5552 2.01348 11.5281C2.13078 11.498 2.71276 11.234 2.77127 11.1754C2.85843 11.1467 3.3533 10.8558 3.44046 10.681C3.49908 10.506 3.61479 9.94989 3.61479 9.77653C3.64419 9.59998 3.67341 9.04526 3.67341 8.90063C3.67341 8.75446 3.64419 8.49207 3.67341 8.37453C3.73221 8.28714 3.81937 8.16937 3.93599 8.1106C4.08093 8.08198 4.25543 8.08198 4.28467 8.08198C4.34346 8.08198 4.83883 8.14075 4.92532 8.14075C5.04189 8.14075 5.50804 8.05336 5.62461 7.993C5.7704 7.96597 5.85689 7.993 5.88702 8.05336C5.94497 8.1106 5.8284 8.19799 5.8284 8.22831C5.79985 8.25676 5.56672 8.54926 5.50804 8.66527C5.44942 8.8116 5.24636 9.07383 5.30516 9.19155C5.36293 9.30756 5.36293 9.33772 5.53745 9.42687C5.71269 9.48235 6.38167 9.83543 6.76048 10.1852C7.16799 10.5618 7.54691 11.0291 7.60577 11.2644C7.69209 11.5283 7.83788 11.5554 7.92521 11.5554C8.01323 11.5855 8.18763 11.6141 8.18763 11.7317C8.18763 11.8479 8.15755 11.9941 8.15755 11.9941L9.23564 12.4028C9.23564 12.4028 9.23564 12.0227 9.2071 11.8781C9.1777 11.7031 9.00246 11.5283 8.85649 11.3518C8.71155 11.1468 7.1983 9.65899 7.05141 9.54139C6.90647 9.45383 6.789 9.33782 6.76052 9.10427C6.73044 8.86895 7.0222 8.69571 7.19828 8.49214C7.314 8.34597 7.63429 7.67365 7.69207 7.58467C7.75001 7.49887 7.95449 7.26509 8.07014 7.26509C8.21609 7.26509 8.53645 7.47025 8.94449 7.61483C9.11899 7.6736 10.3706 7.99318 10.5467 8.05355C10.721 8.08217 11.0984 8.05355 11.2159 8.16956C11.3316 8.25695 11.2159 8.49226 11.1865 8.63689C11.1571 8.81184 10.8377 9.45396 10.8377 9.45396C10.8377 9.45396 10.8956 9.68615 10.8956 9.86423C10.8956 10.0676 10.8084 10.7973 10.7211 11.0293C10.6625 11.2933 10.255 11.9655 10.1669 12.0817C10.1092 12.1691 10.022 12.228 10.022 12.3743C10.022 12.5189 10.2835 12.5508 10.3707 12.6652C10.4881 12.7542 10.4001 12.9274 10.3707 13.104L11.5359 13.5999C11.5359 13.5125 11.5947 13.3662 11.5359 13.3074C11.5058 13.2215 11.3616 13.0452 11.2744 12.9274C11.2158 12.84 11.012 12.5188 10.9541 12.4601C10.8954 12.3743 10.8954 12.1994 10.9541 12.0513C10.9826 11.9069 11.3616 10.9147 11.3901 10.7973C11.4487 10.6526 12.0022 9.48264 12.0599 9.33806C12.1186 9.16328 12.584 8.31617 12.6427 8.16984C12.7006 7.99346 13.0796 7.84866 13.1961 7.84866C13.3411 7.84866 14.0698 8.11101 14.1855 8.16984C14.2726 8.19846 14.9434 8.49254 15.0591 8.54979C15.2051 8.60703 15.1178 8.72474 15.0306 8.75494C14.9434 8.81218 13.8652 9.54186 13.778 9.57201C13.6908 9.60063 13.6037 9.68643 13.5449 9.68643C13.4577 9.68643 13.3411 9.77712 13.3411 9.80573C13.3411 9.86451 13.3126 9.98051 13.1668 9.98051C13.0502 9.98051 12.8172 9.80573 12.6721 9.63077C12.4675 9.74678 12.3224 9.83593 12.2345 10.0091C12.0894 10.1537 12.0308 10.273 11.9434 10.4764C12.0022 10.5352 12.1187 10.5622 12.2051 10.5622H13.1372C13.2246 10.5622 13.341 10.5352 13.429 10.5066C13.5162 10.4478 15.8163 8.9298 15.8163 8.9298C15.8163 8.9298 16.1959 8.63724 16.1959 8.37507C16.1959 8.16992 16.02 8.11114 15.963 8.02375C15.875 7.96651 15.3509 7.61519 15.2637 7.55801C15.1462 7.47061 14.7388 7.2353 14.6231 7.17653C14.535 7.11929 14.214 7.00334 14.214 7.00334C14.3028 6.73941 14.3607 6.53584 14.3892 6.47707C14.4193 6.41829 14.4193 5.89202 14.4472 5.8048C14.4472 5.719 14.5939 5.33906 14.6517 5.28023C14.7103 5.22145 14.8563 4.75413 14.8848 4.66656C14.9149 4.60932 15.1463 3.93704 15.2638 3.93704C15.3795 3.90842 15.4967 4.02443 15.6126 4.02443C15.7299 4.05305 16.1659 3.99581 16.3991 4.02443C16.6035 4.05305 16.8652 4.31699 16.9238 4.34561C17.011 4.37576 17.2141 4.46162 17.2727 4.46162C17.3021 4.46162 17.593 4.25805 17.593 4.20082C17.593 4.14045 17.3884 4.02444 17.3599 4.02444C17.3314 3.99582 17.1276 3.87811 17.1276 3.87811C17.1276 3.90849 17.2141 3.82088 17.3599 3.82088C17.4765 3.82088 17.593 3.93706 17.6216 3.9672C17.6802 3.99582 17.8261 4.08321 17.8545 4.02444C17.8835 3.9952 18.0001 3.84887 18.0001 3.76165C18.0001 3.64405 17.8835 3.49947 17.8256 3.44047C17.7678 3.3817 17.0978 2.74112 17.0392 2.71096C16.952 2.68234 16.6031 2.35946 16.6031 2.27384C16.5731 2.2133 16.3987 2.06868 16.3702 2.03852C16.3399 2.0099 15.4673 1.398 15.3507 1.30724C15.2342 1.22144 15.1176 1.07522 15.1176 0.929055C15.1176 0.784426 15.059 0.404482 15.059 0.404482C15.059 0.404482 14.8561 0.375862 14.7681 0.46172C14.6816 0.522081 14.4772 0.989419 14.3606 0.989419C14.0394 0.929058 13.6318 1.01804 13.6318 1.07522C13.6619 1.16261 13.9529 1.10384 13.9529 1.10384C13.9529 1.13399 13.8363 1.27861 13.7482 1.27861C13.6905 1.27861 13.516 1.33739 13.4867 1.36601C13.4288 1.36601 13.3122 1.45669 13.3122 1.45669C13.3987 1.45669 13.4867 1.45669 13.6033 1.48355C13.6033 1.48355 13.4288 1.60132 13.3407 1.60132C13.2544 1.60132 12.9627 1.68871 12.8754 1.71733C12.8168 1.77457 12.5836 1.95105 12.5836 1.95105H12.3219L12.3505 2.00983H12.4964V2.03862L12.4967 2.03868Z"
                              fill="black">
                        </path>
                    </svg>
                    <span className="text-xxsmall text-bold">Kupon Görüntüle</span>
                    <span
                        className="text-xxsmall text-bold">{CurrencyFormat(totalAmount)}₺</span>
                </div>
            </div>
        }
        {
            isOpen && <div>
                <div className="coupon-wrapper">
                    <div className="coupon-header">
                        <div className="d-flex justify-content-center align-items-center">
                            <span className="text-xxsmall text-bold">{longtermSelection?.hippodrome.hippodrome || 'Uzun Vadeli'}</span>
                        </div>

                        <div className="hide-button" onClick={() => {
                            setIsOpen(false);
                        }}>
                            <span className="text-xxsmall text-bold">Gizle</span>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6L8 10L12 6" stroke="black" strokeWidth="1.5" strokeLinecap="round"
                                      strokeLinejoin="round">
                                </path>
                            </svg>
                        </div>
                    </div>
                    <div className="coupon-body">
                        {longtermSelection &&
                            <LongtermSelectionRow selection={longtermSelection}/>
                        }

                    </div>
                    {LongtermBetslip.getOptions().errorMessage &&
                        <div className="error-wrapper">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 12 12"
                                 fill="none">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M6 0.5C2.96243 0.5 0.5 2.96243 0.5 6C0.5 9.03757 2.96243 11.5 6 11.5C9.03757 11.5 11.5 9.03757 11.5 6C11.5 2.96243 9.03757 0.5 6 0.5ZM6 3.5C5.72386 3.5 5.5 3.72386 5.5 4C5.5 4.27614 5.72386 4.5 6 4.5H6.005C6.28114 4.5 6.505 4.27614 6.505 4C6.505 3.72386 6.28114 3.5 6.005 3.5H6ZM6.5 6C6.5 5.72386 6.27614 5.5 6 5.5C5.72386 5.5 5.5 5.72386 5.5 6V8C5.5 8.27614 5.72386 8.5 6 8.5C6.27614 8.5 6.5 8.27614 6.5 8V6Z"
                                      fill="#FFE700">
                                </path>
                            </svg>
                            <p className="text-xsmall text-white">{LongtermBetslip.getOptions().errorMessage}</p>
                        </div>
                    }
                    <div>
                        <div className="selected-horse-list">
                            <div className="slip-icon-wrapper">
                                <div className="slip-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                                         viewBox="0 0 18 14"
                                         fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd"
                                              d="M12.4967 2.03868C12.5259 2.06883 12.4673 2.09745 12.4673 2.09745C12.4673 2.09745 12.2636 2.15622 12.2049 2.24362C12.1463 2.30256 12.0598 2.50596 12.0598 2.50596C12.1763 2.47734 12.3222 2.44719 12.4673 2.38819C12.4967 2.38819 12.5259 2.41857 12.5553 2.44713C12.5839 2.44713 12.2049 2.5933 12.1184 2.65053C12.0598 2.71106 11.7688 2.94461 11.6816 2.94461C11.5945 2.94461 11.3898 2.97323 11.3898 2.97323C11.4192 3.032 11.4485 3.08924 11.5055 3.17663C11.5055 3.17663 11.3027 3.2944 11.3027 3.32295C11.2741 3.35311 11.1282 3.52811 11.1282 3.52811C11.1282 3.52811 11.6523 3.58518 11.8268 3.29439C11.6523 3.58518 11.2447 3.87774 10.3998 3.87774C9.52616 3.87774 8.82791 3.52817 8.36189 3.35316C7.86634 3.14976 7.22603 2.91444 6.70181 2.91444C6.20712 2.91444 5.62458 3.06061 5.24632 3.41193C4.57645 3.14976 3.64438 3.29433 3.20736 3.7029C2.79985 4.11146 2.39237 4.60895 2.33369 4.95715C2.24652 5.33862 2.0428 6.24297 1.75166 6.6533C1.34346 7.32228 0.877986 7.49881 0.732191 7.78961C0.586396 8.08216 0.586396 8.69566 0.614891 8.69566C0.673513 8.72428 0.878149 8.69566 0.994553 8.63671C1.22768 8.54932 1.86748 7.87859 2.10049 7.55748C2.33345 7.23477 3.1208 5.83298 3.23652 5.33858C3.35309 4.84263 3.46948 4.34514 3.81924 4.0828C4.19822 3.82045 4.66278 3.93663 4.66278 3.96679C4.66278 3.96679 4.22677 4.31653 4.19822 5.2798C4.16815 6.24292 4.75085 6.1269 4.45973 6.739C4.16809 7.26494 3.29532 6.91378 3.00438 7.23478C2.77125 7.49871 3.00438 8.43162 3.00438 8.6064C3.00438 8.78295 2.91563 9.59984 2.85859 9.805C2.82851 9.97977 2.85859 10.0958 2.82851 10.2136C2.79996 10.3025 3.00461 10.3296 3.00461 10.3296C3.00461 10.3296 2.82851 10.5616 2.74135 10.5059C2.65418 10.4758 2.59624 10.4758 2.5383 10.5059C2.47968 10.5059 2.36397 10.5616 2.24666 10.5059L1.78035 11.498C1.83914 11.5281 1.92631 11.5552 2.01348 11.5281C2.13078 11.498 2.71276 11.234 2.77127 11.1754C2.85843 11.1467 3.3533 10.8558 3.44046 10.681C3.49908 10.506 3.61479 9.94989 3.61479 9.77653C3.64419 9.59998 3.67341 9.04526 3.67341 8.90063C3.67341 8.75446 3.64419 8.49207 3.67341 8.37453C3.73221 8.28714 3.81937 8.16937 3.93599 8.1106C4.08093 8.08198 4.25543 8.08198 4.28467 8.08198C4.34346 8.08198 4.83883 8.14075 4.92532 8.14075C5.04189 8.14075 5.50804 8.05336 5.62461 7.993C5.7704 7.96597 5.85689 7.993 5.88702 8.05336C5.94497 8.1106 5.8284 8.19799 5.8284 8.22831C5.79985 8.25676 5.56672 8.54926 5.50804 8.66527C5.44942 8.8116 5.24636 9.07383 5.30516 9.19155C5.36293 9.30756 5.36293 9.33772 5.53745 9.42687C5.71269 9.48235 6.38167 9.83543 6.76048 10.1852C7.16799 10.5618 7.54691 11.0291 7.60577 11.2644C7.69209 11.5283 7.83788 11.5554 7.92521 11.5554C8.01323 11.5855 8.18763 11.6141 8.18763 11.7317C8.18763 11.8479 8.15755 11.9941 8.15755 11.9941L9.23564 12.4028C9.23564 12.4028 9.23564 12.0227 9.2071 11.8781C9.1777 11.7031 9.00246 11.5283 8.85649 11.3518C8.71155 11.1468 7.1983 9.65899 7.05141 9.54139C6.90647 9.45383 6.789 9.33782 6.76052 9.10427C6.73044 8.86895 7.0222 8.69571 7.19828 8.49214C7.314 8.34597 7.63429 7.67365 7.69207 7.58467C7.75001 7.49887 7.95449 7.26509 8.07014 7.26509C8.21609 7.26509 8.53645 7.47025 8.94449 7.61483C9.11899 7.6736 10.3706 7.99318 10.5467 8.05355C10.721 8.08217 11.0984 8.05355 11.2159 8.16956C11.3316 8.25695 11.2159 8.49226 11.1865 8.63689C11.1571 8.81184 10.8377 9.45396 10.8377 9.45396C10.8377 9.45396 10.8956 9.68615 10.8956 9.86423C10.8956 10.0676 10.8084 10.7973 10.7211 11.0293C10.6625 11.2933 10.255 11.9655 10.1669 12.0817C10.1092 12.1691 10.022 12.228 10.022 12.3743C10.022 12.5189 10.2835 12.5508 10.3707 12.6652C10.4881 12.7542 10.4001 12.9274 10.3707 13.104L11.5359 13.5999C11.5359 13.5125 11.5947 13.3662 11.5359 13.3074C11.5058 13.2215 11.3616 13.0452 11.2744 12.9274C11.2158 12.84 11.012 12.5188 10.9541 12.4601C10.8954 12.3743 10.8954 12.1994 10.9541 12.0513C10.9826 11.9069 11.3616 10.9147 11.3901 10.7973C11.4487 10.6526 12.0022 9.48264 12.0599 9.33806C12.1186 9.16328 12.584 8.31617 12.6427 8.16984C12.7006 7.99346 13.0796 7.84866 13.1961 7.84866C13.3411 7.84866 14.0698 8.11101 14.1855 8.16984C14.2726 8.19846 14.9434 8.49254 15.0591 8.54979C15.2051 8.60703 15.1178 8.72474 15.0306 8.75494C14.9434 8.81218 13.8652 9.54186 13.778 9.57201C13.6908 9.60063 13.6037 9.68643 13.5449 9.68643C13.4577 9.68643 13.3411 9.77712 13.3411 9.80573C13.3411 9.86451 13.3126 9.98051 13.1668 9.98051C13.0502 9.98051 12.8172 9.80573 12.6721 9.63077C12.4675 9.74678 12.3224 9.83593 12.2345 10.0091C12.0894 10.1537 12.0308 10.273 11.9434 10.4764C12.0022 10.5352 12.1187 10.5622 12.2051 10.5622H13.1372C13.2246 10.5622 13.341 10.5352 13.429 10.5066C13.5162 10.4478 15.8163 8.9298 15.8163 8.9298C15.8163 8.9298 16.1959 8.63724 16.1959 8.37507C16.1959 8.16992 16.02 8.11114 15.963 8.02375C15.875 7.96651 15.3509 7.61519 15.2637 7.55801C15.1462 7.47061 14.7388 7.2353 14.6231 7.17653C14.535 7.11929 14.214 7.00334 14.214 7.00334C14.3028 6.73941 14.3607 6.53584 14.3892 6.47707C14.4193 6.41829 14.4193 5.89202 14.4472 5.8048C14.4472 5.719 14.5939 5.33906 14.6517 5.28023C14.7103 5.22145 14.8563 4.75413 14.8848 4.66656C14.9149 4.60932 15.1463 3.93704 15.2638 3.93704C15.3795 3.90842 15.4967 4.02443 15.6126 4.02443C15.7299 4.05305 16.1659 3.99581 16.3991 4.02443C16.6035 4.05305 16.8652 4.31699 16.9238 4.34561C17.011 4.37576 17.2141 4.46162 17.2727 4.46162C17.3021 4.46162 17.593 4.25805 17.593 4.20082C17.593 4.14045 17.3884 4.02444 17.3599 4.02444C17.3314 3.99582 17.1276 3.87811 17.1276 3.87811C17.1276 3.90849 17.2141 3.82088 17.3599 3.82088C17.4765 3.82088 17.593 3.93706 17.6216 3.9672C17.6802 3.99582 17.8261 4.08321 17.8545 4.02444C17.8835 3.9952 18.0001 3.84887 18.0001 3.76165C18.0001 3.64405 17.8835 3.49947 17.8256 3.44047C17.7678 3.3817 17.0978 2.74112 17.0392 2.71096C16.952 2.68234 16.6031 2.35946 16.6031 2.27384C16.5731 2.2133 16.3987 2.06868 16.3702 2.03852C16.3399 2.0099 15.4673 1.398 15.3507 1.30724C15.2342 1.22144 15.1176 1.07522 15.1176 0.929055C15.1176 0.784426 15.059 0.404482 15.059 0.404482C15.059 0.404482 14.8561 0.375862 14.7681 0.46172C14.6816 0.522081 14.4772 0.989419 14.3606 0.989419C14.0394 0.929058 13.6318 1.01804 13.6318 1.07522C13.6619 1.16261 13.9529 1.10384 13.9529 1.10384C13.9529 1.13399 13.8363 1.27861 13.7482 1.27861C13.6905 1.27861 13.516 1.33739 13.4867 1.36601C13.4288 1.36601 13.3122 1.45669 13.3122 1.45669C13.3987 1.45669 13.4867 1.45669 13.6033 1.48355C13.6033 1.48355 13.4288 1.60132 13.3407 1.60132C13.2544 1.60132 12.9627 1.68871 12.8754 1.71733C12.8168 1.77457 12.5836 1.95105 12.5836 1.95105H12.3219L12.3505 2.00983H12.4964V2.03862L12.4967 2.03868Z"
                                              fill="black">
                                        </path>
                                    </svg>
                                </div>
                            </div>
                            <div className="d-flex justify-content-center align-items-center">
                                <span
                                    className="text-xsmall text-secondary">{(() => {
                                    const sel: any = LongtermBetslip.getSelection();
                                    return sel ? `${sel.hippodrome?.hippodrome} - ${sel.horse?.description}` : '';
                                })()}</span>
                            </div>
                        </div>
                    </div>
                    <div className="slip-balance-wrapper">
                        <div className="balance-left">
                            <span className="text-xxsmall text-white">Bakiye</span>
                            <span
                                className="text-xxsmall text-white text-bold">{auth.member ? auth.member.balance_debit : '-'}</span>
                        </div>

                        {auth.member && auth.member.is_editor &&
                            <div className="balance-left" style={{paddingLeft: '10px'}}>
                                            <span
                                                className="text-xxsmall text-white d-flex align-items-center">Paylaş</span>
                                <button onClick={() => {
                                    LongtermBetslip.setOptions({shareCoupon: !LongtermBetslip.getOptions().shareCoupon});
                                }}
                                        className={`button xs primary filled betting-mobile-point-checkbox p-0 ${LongtermBetslip.getOptions().shareCoupon ? 'point-selected' : ''}`}>
                                    {LongtermBetslip.getOptions().shareCoupon &&
                                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"
                                             viewBox="0 0 8 8"
                                             fill="none">
                                            <path d="M6.66658 2L2.99992 5.66667L1.33325 4" stroke="white"
                                                  strokeWidth="1.6"
                                                  strokeLinecap="round" strokeLinejoin="round"></path>
                                        </svg>}
                                </button>

                            </div>}
                        <div className="balance-right">
                            <span className="text-xxsmall text-white d-flex align-items-center">Puan</span>
                            <span
                                className="text-xxsmall text-white text-bold">{auth.member ? auth.member.balance_point : '-'}</span>
                            {auth.member && auth.authentication && <button onClick={() => {
                                LongtermBetslip.setOptions({usePointBalance: !LongtermBetslip.getOptions().usePointBalance})
                            }}
                                                                           className={`button xs primary filled betting-mobile-point-checkbox p-0 ${LongtermBetslip.getOptions().usePointBalance ? 'point-selected' : ''}`}>
                                {LongtermBetslip.getOptions().usePointBalance &&
                                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"
                                         viewBox="0 0 8 8"
                                         fill="none">
                                        <path d="M6.66658 2L2.99992 5.66667L1.33325 4" stroke="white"
                                              strokeWidth="1.6"
                                              strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>}
                            </button>}
                        </div>
                    </div>
                    <AcceptOddsChange
                        checked={LongtermBetslip.getOptions().acceptChangedOdds}
                        onChange={() => LongtermBetslip.setOptions({acceptChangedOdds: !LongtermBetslip.getOptions().acceptChangedOdds})}/>
                    <div className="slip-action-wrapper">
                        <button className="button primary filled action-delete" onClick={() => {
                            LongtermBetslip.clear();
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18"
                                 fill="none">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M8.37109 0.750001H9.62891C10.0242 0.749988 10.3654 0.749978 10.6463 0.77293C10.9428 0.797155 11.2377 0.850642 11.5215 0.995238C11.9448 1.21095 12.289 1.55516 12.5048 1.97852C12.6494 2.26231 12.7028 2.55722 12.7271 2.85373C12.7476 3.10556 12.7498 3.40573 12.75 3.75H15.75C16.1642 3.75 16.5 4.08579 16.5 4.5C16.5 4.91422 16.1642 5.25 15.75 5.25H15V12.931C15 13.5347 15 14.033 14.9669 14.4389C14.9324 14.8604 14.8585 15.248 14.673 15.612C14.3854 16.1765 13.9265 16.6354 13.362 16.923C12.998 17.1085 12.6104 17.1824 12.1889 17.2169C11.783 17.25 11.2847 17.25 10.681 17.25H7.31901C6.71529 17.25 6.21702 17.25 5.81113 17.2169C5.38956 17.1824 5.00203 17.1085 4.63803 16.923C4.07354 16.6354 3.6146 16.1765 3.32698 15.612C3.14151 15.248 3.06759 14.8604 3.03315 14.4389C2.99998 14.033 2.99999 13.5347 3 12.931L3 5.25H2.25C1.83579 5.25 1.5 4.91422 1.5 4.5C1.5 4.08579 1.83579 3.75 2.25 3.75H5.25002C5.25023 3.40573 5.25235 3.10556 5.27293 2.85373C5.29715 2.55722 5.35064 2.26231 5.49524 1.97852C5.71095 1.55516 6.05516 1.21095 6.47852 0.995238C6.76231 0.850642 7.05722 0.797155 7.35373 0.77293C7.63464 0.749978 7.97576 0.749988 8.37109 0.750001ZM7.5 7.875C7.91421 7.875 8.25 8.21079 8.25 8.625V12.375C8.25 12.7892 7.91421 13.125 7.5 13.125C7.08579 13.125 6.75 12.7892 6.75 12.375V8.625C6.75 8.21079 7.08579 7.875 7.5 7.875ZM11.25 8.625C11.25 8.21079 10.9142 7.875 10.5 7.875C10.0858 7.875 9.75 8.21079 9.75 8.625V12.375C9.75 12.7892 10.0858 13.125 10.5 13.125C10.9142 13.125 11.25 12.7892 11.25 12.375V8.625Z"
                                      fill="url(#paint0_linear_6725_145842)">
                                </path>
                                <defs>
                                    <linearGradient id="paint0_linear_6725_145842" x1="9" y1="0.75" x2="9"
                                                    y2="17.25"
                                                    gradientUnits="userSpaceOnUse">
                                        <stop stopColor="white">
                                        </stop>
                                        <stop offset="1" stopColor="white" stopOpacity="0.7">
                                        </stop>
                                    </linearGradient>
                                </defs>
                            </svg>
                        </button>
                        <button className="button tertiary filled action-save" onClick={() => {
                            playCoupon(true);
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18"
                                 fill="none">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M11.9039 1.56224C11.6426 1.4995 11.3735 1.49975 11.0684 1.50004L5.81903 1.50008C5.2153 1.50007 4.71703 1.50006 4.31113 1.53322C3.88956 1.56767 3.50203 1.64159 3.13803 1.82706C2.57354 2.11468 2.1146 2.57362 1.82698 3.1381C1.64151 3.50211 1.56759 3.88963 1.53315 4.31121C1.49998 4.7171 1.49999 5.21537 1.5 5.81909V12.1811C1.49999 12.7848 1.49998 13.2831 1.53315 13.6889C1.56759 14.1105 1.64151 14.498 1.82698 14.862C2.1146 15.4265 2.57354 15.8855 3.13803 16.1731C3.50203 16.3586 3.88956 16.4325 4.31113 16.4669C4.71702 16.5001 5.21529 16.5001 5.81901 16.5001H12.181C12.7847 16.5001 13.283 16.5001 13.6889 16.4669C14.1104 16.4325 14.498 16.3586 14.862 16.1731C15.4265 15.8855 15.8854 15.4265 16.173 14.862C16.3585 14.498 16.4324 14.1105 16.4669 13.6889C16.5 13.2831 16.5 12.7848 16.5 12.1811L16.5 6.93172C16.5003 6.62654 16.5006 6.35748 16.4378 6.09614C16.3827 5.86656 16.2918 5.64709 16.1684 5.44577C16.028 5.21662 15.8376 5.02654 15.6216 4.81095L13.1891 2.37848C12.9735 2.16249 12.7835 1.97206 12.5543 1.83164C12.353 1.70827 12.1335 1.61736 11.9039 1.56224ZM11.5538 3.02072C11.4875 3.00482 11.4085 3 11.0059 3H5.85C5.62662 3 5.42812 3.00007 5.25 3.00132V4.79992C5.25 5.21996 5.25 5.42998 5.33175 5.59042C5.40365 5.73154 5.51839 5.84627 5.65951 5.91818C5.81994 5.99992 6.02996 5.99992 6.45 5.99992H11.55C11.97 5.99992 12.1801 5.99992 12.3405 5.91818C12.4816 5.84627 12.5963 5.73154 12.6683 5.59042C12.75 5.42998 12.75 5.21996 12.75 4.79992V4.06066L12.1726 3.48327C11.8879 3.1986 11.8286 3.14611 11.7706 3.11052C11.7035 3.0694 11.6303 3.03909 11.5538 3.02072ZM6.45 9.74992H11.55C11.97 9.74992 12.1801 9.74992 12.3405 9.83167C12.4816 9.90357 12.5963 10.0183 12.6683 10.1594C12.75 10.3199 12.75 10.5299 12.75 10.9499V14.9999H5.25V10.9499C5.25 10.5299 5.25 10.3199 5.33175 10.1594C5.40365 10.0183 5.51839 9.90357 5.65951 9.83167C5.81994 9.74992 6.02996 9.74992 6.45 9.74992Z"
                                      fill="url(#paint0_linear_6725_145844)">
                                </path>
                                <defs>
                                    <linearGradient id="paint0_linear_6725_145844" x1="9.00004" y1="1.5"
                                                    x2="9.00004"
                                                    y2="16.5001" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="white">
                                        </stop>
                                        <stop offset="1" stopColor="white" stopOpacity="0.7">
                                        </stop>
                                    </linearGradient>
                                </defs>
                            </svg>
                        </button>
                        <div className="action-multipy-wrapper">
                            <span
                                className="text-xsmall text-white d-flex justify-content-center align-items-center w-100">Misli</span>
                            <input
                                type="number" className={"multipy-input hide-stepper"}
                                pattern="^[0-9\b]+$" min={20} max={betSlipMaxMultiply}
                                step={1} value={LongtermBetslip.getOptions().multiply || ''}
                                onKeyDown={preventBetSlipValues}
                                onChange={e => {
                                    if (Number(e.target.value) <= 12000) {
                                        LongtermBetslip.setOptions({multiply: Number(e.target.value)})
                                    } else {
                                        LongtermBetslip.setOptions({multiply: 12000})
                                    }
                                }}
                                onBlur={() => {
                                    // Uzun vadelide asgari misli 20.
                                    if (!LongtermBetslip.getOptions().multiply || Number(LongtermBetslip.getOptions().multiply) < 20) {
                                        LongtermBetslip.setOptions({multiply: 20})
                                    }

                                }}
                                onPaste={(e) => {
                                    e.preventDefault()
                                    return false;
                                }}/>
                        </div>
                        {canPlayNow() ? <button
                            className={`button tertiary outlined play-button ${LongtermBetslip.getOptions().errorMessage || betCount === 0 || !LongtermBetslip.getOptions().acceptChangedOdds || hasStaleItem ? '' : 'play-active'}`}
                            disabled={LongtermBetslip.getOptions().errorMessage || betCount === 0 || !LongtermBetslip.getOptions().acceptChangedOdds || hasStaleItem ? true : false}
                            onClick={() => {
                                confirmCoupon();
                            }}
                        >
                            {LongtermBetslip.getOptions().inProgress ? <>
                                <span className="text-small text-white text-bolder"
                                      style={{letterSpacing: "-0.3px"}}>OYNANIYOR</span>
                            </> : <>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>OYNA</span>
                                <span className="text-xxsmall text-white text-bold">
                                {CurrencyFormat(totalAmount)}₺</span></>}
                        </button> : <button
                            className={`button tertiary outlined play-button `}
                            disabled={true}>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>Bahisler Açılmadı</span>
                        </button>
                        }
                    </div>
                </div>
            </div>
        }
    </div>
}

export function LongtermBetslipPage(prop: any) {
    const frame = useBetslipFrame();
    const {navigate, betCount, betSlipMaxMultiply, scheduleErrorClear, hasStaleItem} = frame;
    const totalAmount = frame.totalAmount;

    if (!frame.ready) {
        return <></>;
    }

    const longtermSelection = LongtermBetslip.getSelection();


    const playCoupon = (save: boolean = false, imgData?: CouponImage) => playCouponFlow(save, {
        navigate,
        totalAmount,
        afterSave: (bulletinUrl: string) => ({
            label: "Bülten'e Dön",
            action: () => {
                if (isMobile) {
                    navigate(bulletinUrl);
                }
            }
        }),
    }, imgData);


    const confirmCoupon = () => {
        if (!(auth.authentication && auth.member && auth.member.id)) {
            navigate(`${paths.login.url}/${window.location.pathname.replaceAll('/', '|')}`);
            return;
        }
        const errorMessage = validateLongtermCoupon(betCount, totalAmount);
        if (errorMessage) {
            LongtermBetslip.setOptions({errorMessage});
            scheduleErrorClear();
            return;
        }
        playCoupon(false);
    };

    return <div className={"page-widget betslip-page"} style={{paddingTop: "1px"}}>
        <div className={"betting-info"}>
            <div className={"d-flex flex-column"}>
                <div className={"betslip-type-info"}>{longtermSelection?.hippodrome.hippodrome || 'Uzun Vadeli'}</div>
            </div>
            <div>
                <img alt={"info"} src={infoIcon}/>
            </div>
        </div>
        <div className="coupon-body">
            {longtermSelection &&
                <LongtermSelectionRow selection={longtermSelection}/>
            }
        </div>
        {LongtermBetslip.getOptions().errorMessage && <div className="betslip-mobile-coupon-error-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 12 12"
                 fill="none">
                <path fillRule="evenodd" clipRule="evenodd"
                      d="M6 0.5C2.96243 0.5 0.5 2.96243 0.5 6C0.5 9.03757 2.96243 11.5 6 11.5C9.03757 11.5 11.5 9.03757 11.5 6C11.5 2.96243 9.03757 0.5 6 0.5ZM6 3.5C5.72386 3.5 5.5 3.72386 5.5 4C5.5 4.27614 5.72386 4.5 6 4.5H6.005C6.28114 4.5 6.505 4.27614 6.505 4C6.505 3.72386 6.28114 3.5 6.005 3.5H6ZM6.5 6C6.5 5.72386 6.27614 5.5 6 5.5C5.72386 5.5 5.5 5.72386 5.5 6V8C5.5 8.27614 5.72386 8.5 6 8.5C6.27614 8.5 6.5 8.27614 6.5 8V6Z"
                      fill="#FFE700"></path>
            </svg>
            <p className="text-xsmall text-white ">{LongtermBetslip.getOptions().errorMessage}</p>
        </div>}
        <div className={"play-container"}>
            <div className={"row"}>
                {<div className={"col-6 d-flex align-items-center justify-content-between"}>
                        <div className={"play-container-label"}>Bahis</div>
                        <div
                            className={"play-container-value"}>{betCount}</div>
                    </div>}
                <div className={"col-6 d-flex align-items-center justify-content-between"}>
                    <div className={"play-container-label"}>Tutar</div>
                    <div
                        className={"play-container-value"}>{CurrencyFormat(totalAmount) + '₺'}</div>
                </div>
            </div>
            <div className={"row"}>
                <div className={"col-6 d-flex align-items-center justify-content-between"}>
                    <div className={"play-container-label"}>Bilet Adedi</div>
                    <div className={"play-container-value"}>1</div>
                </div>
                <div className={"col-6 d-flex align-items-center justify-content-between"}>
                    <div className={"play-container-label"}>Misli</div>
                    <div className={"play-container-value w-50"}><input
                        type="number"
                        className={"multipy-input betslip-page-multipy-input hide-stepper"}
                        pattern="^[0-9\b]+$" min={20} max={betSlipMaxMultiply}
                        step={1} value={LongtermBetslip.getOptions().multiply || ''}
                        onKeyDown={preventBetSlipValues}
                        onChange={e => {
                            if (Number(e.target.value) <= 12000) {
                                LongtermBetslip.setOptions({multiply: Number(e.target.value)})
                            } else {
                                LongtermBetslip.setOptions({multiply: 12000})
                            }
                        }}
                        onBlur={() => {
                            // Uzun vadelide asgari misli 20.
                            if (!LongtermBetslip.getOptions().multiply || Number(LongtermBetslip.getOptions().multiply) < 20) {
                                LongtermBetslip.setOptions({multiply: 20})
                            }
                        }}
                        onPaste={(e) => {
                            e.preventDefault()
                            return false;
                        }}
                    /></div>
                </div>
            </div>
            <AcceptOddsChange
                tone={"light"}
                checked={LongtermBetslip.getOptions().acceptChangedOdds}
                onChange={() => LongtermBetslip.setOptions({acceptChangedOdds: !LongtermBetslip.getOptions().acceptChangedOdds})}/>
            <div className={"row play-container-actions"}>
                <div className={"col-2 p-0"}>
                    <div className={`btn delete-btn w-100 `}
                         onClick={() => {
                             LongtermBetslip.clear();
                         }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                             viewBox="0 0 17 16" fill="none">
                            <path
                                d="M3.83329 5.33325V11.9999C3.83329 13.4727 5.0272 14.6666 6.49996 14.6666H10.5C11.9727 14.6666 13.1666 13.4727 13.1666 11.9999V5.33325M9.83329 7.33325V11.3333M7.16663 7.33325L7.16663 11.3333M11.1666 3.33325L10.2291 1.92698C9.98183 1.55605 9.56552 1.33325 9.11971 1.33325H7.88021C7.4344 1.33325 7.01809 1.55605 6.7708 1.92698L5.83329 3.33325M11.1666 3.33325H5.83329M11.1666 3.33325H14.5M5.83329 3.33325H2.49996"
                                stroke="#D83131" strokeLinecap="round"
                                strokeLinejoin="round"></path>
                        </svg>
                    </div>
                </div>
                <div className={"col-8"}>
                    {canPlayNow() ? <button
                        className={`button  w-100 h-100 justify-content-center play-inner ${LongtermBetslip.getOptions().errorMessage || betCount === 0 || !LongtermBetslip.getOptions().acceptChangedOdds || hasStaleItem ? '' : 'play-active'}`}

                        disabled={LongtermBetslip.getOptions().errorMessage || betCount === 0 || !LongtermBetslip.getOptions().acceptChangedOdds || hasStaleItem ? true : false}
                        onClick={() => {
                            confirmCoupon();
                        }}
                    >
                        {LongtermBetslip.getOptions().inProgress ? <>
                                <span className="text-small text-white text-bolder"
                                      style={{letterSpacing: "-0.3px"}}>OYNANIYOR</span>
                        </> : <>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>OYNA</span>
                        </>}
                    </button> : <button
                        className={`button tertiary outlined play-button `}
                        disabled={true}>
                            <span className="text-small text-white text-bolder"
                                  style={{letterSpacing: "-0.3px"}}>Bahisler Açılmadı</span>
                    </button>}
                </div>
                <div className={"col-2 p-0"}>
                    <div className={`btn delete-btn w-100 `}
                         onClick={() => {
                             playCoupon(true);
                         }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                             viewBox="0 0 18 18" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M11.9039 1.56224C11.6426 1.4995 11.3735 1.49975 11.0684 1.50004L5.81903 1.50008C5.2153 1.50007 4.71703 1.50006 4.31113 1.53322C3.88956 1.56767 3.50203 1.64159 3.13803 1.82706C2.57354 2.11468 2.1146 2.57362 1.82698 3.1381C1.64151 3.50211 1.56759 3.88963 1.53315 4.31121C1.49998 4.7171 1.49999 5.21537 1.5 5.81909V12.1811C1.49999 12.7848 1.49998 13.2831 1.53315 13.6889C1.56759 14.1105 1.64151 14.498 1.82698 14.862C2.1146 15.4265 2.57354 15.8855 3.13803 16.1731C3.50203 16.3586 3.88956 16.4325 4.31113 16.4669C4.71702 16.5001 5.21529 16.5001 5.81901 16.5001H12.181C12.7847 16.5001 13.283 16.5001 13.6889 16.4669C14.1104 16.4325 14.498 16.3586 14.862 16.1731C15.4265 15.8855 15.8854 15.4265 16.173 14.862C16.3585 14.498 16.4324 14.1105 16.4669 13.6889C16.5 13.2831 16.5 12.7848 16.5 12.1811L16.5 6.93172C16.5003 6.62654 16.5006 6.35748 16.4378 6.09614C16.3827 5.86656 16.2918 5.64709 16.1684 5.44577C16.028 5.21662 15.8376 5.02654 15.6216 4.81095L13.1891 2.37848C12.9735 2.16249 12.7835 1.97206 12.5543 1.83164C12.353 1.70827 12.1335 1.61736 11.9039 1.56224ZM11.5538 3.02072C11.4875 3.00482 11.4085 3 11.0059 3H5.85C5.62662 3 5.42812 3.00007 5.25 3.00132V4.79992C5.25 5.21996 5.25 5.42998 5.33175 5.59042C5.40365 5.73154 5.51839 5.84627 5.65951 5.91818C5.81994 5.99992 6.02996 5.99992 6.45 5.99992H11.55C11.97 5.99992 12.1801 5.99992 12.3405 5.91818C12.4816 5.84627 12.5963 5.73154 12.6683 5.59042C12.75 5.42998 12.75 5.21996 12.75 4.79992V4.06066L12.1726 3.48327C11.8879 3.1986 11.8286 3.14611 11.7706 3.11052C11.7035 3.0694 11.6303 3.03909 11.5538 3.02072ZM6.45 9.74992H11.55C11.97 9.74992 12.1801 9.74992 12.3405 9.83167C12.4816 9.90357 12.5963 10.0183 12.6683 10.1594C12.75 10.3199 12.75 10.5299 12.75 10.9499V14.9999H5.25V10.9499C5.25 10.5299 5.25 10.3199 5.33175 10.1594C5.40365 10.0183 5.51839 9.90357 5.65951 9.83167C5.81994 9.74992 6.02996 9.74992 6.45 9.74992Z"
                                  fill="url(#paint0_linear_6725_145844)">
                            </path>
                            <defs>
                                <linearGradient id="paint0_linear_6725_145844" x1="9.00004" y1="1.5"
                                                x2="9.00004"
                                                y2="16.5001" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="black">
                                    </stop>
                                    <stop offset="1" stopColor="black" stopOpacity="0.7">
                                    </stop>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </div>
}


// ===== TÜRE ÖZGÜ: onay modalı ve seçim çizimi =====

export interface LongtermConfirmContext {
    totalAmount: number;
    playCoupon: (save?: boolean) => void;
}

/** Kupon onay modalını kurar ve gösterir. Eskiden betslip.tsx'teki dev if/else zincirindeydi. */
export function showLongtermConfirmModal(ctx: LongtermConfirmContext) {
    const {totalAmount, playCoupon} = ctx;
    const longtermSelection = LongtermBetslip.getSelection();

    if (!longtermSelection) {
        LongtermBetslip.setOptions({errorMessage: 'Kuponunuzda oynanacak bir bahis bulunmamaktadır.'});
    } else {
        // Misli, gönderimin kullandığı KAYNAKTAN okunuyor (servisin kendi seçenekleri) -
        // ekranda gösterilen ile kupona giden değer ayrışmasın.
        const maxWin = LongtermBetslip.getMaxWin();
        modalServiceSubject.next({
            title: `BAHİS DETAYI - ${longtermSelection.hippodrome.hippodrome}`,
            width: '960px',
            greenBg: true,
            inlineFooter: true,
            innerHtml: true,
            content: `<div class="ticket-modal-body px-1 py-1">
                <div class="ticket-detail-row py-2" style="border-bottom: 1px solid #e8e6d0">
                    <div class="d-flex justify-content-between w-100 me-2">
                        <p class="text-small">Hipodrom</p>
                        <p class="text-small text-bold">${longtermSelection.hippodrome.hippodrome}</p>
                    </div>
                    <div class="d-flex justify-content-between w-100 ms-2">
                        <p class="text-small">Bahis Türü</p>
                        <p class="text-small text-bold">${longtermBettingType.name}</p>
                    </div>
                </div>
                <div class="ticket-detail-row py-2" style="border-bottom: 1px solid #e8e6d0">
                    <div class="d-flex justify-content-between w-100 me-2">
                        <p class="text-small">Seçim</p>
                        <p class="text-small text-bold">${longtermSelection.horse.description} (${Number(longtermSelection.horse.win_odds).toFixed(2)})</p>
                    </div>
                    <div class="d-flex justify-content-between w-100 ms-2">
                        <p class="text-small">Misli</p>
                        <p class="text-small text-bold">${LongtermBetslip.getOptions().multiply}</p>
                    </div>
                </div>
                <div class="ticket-detail-row py-2">
                    <div class="d-flex justify-content-between w-100 me-2">
                        <p class="text-small">Tutar</p>
                        <p class="text-small text-bold">${CurrencyFormat(totalAmount)}₺</p>
                    </div>
                    <div class="d-flex justify-content-between w-100 ms-2">
                        <p class="text-small">Maks. Kazanç</p>
                        <p class="text-small text-bold">${CurrencyFormat(maxWin)}₺</p>
                    </div>
                </div>
            </div>`,
            confirm: {
                sure: {
                    class: 'btn-success fw-bold',
                    label: 'Onayla',
                    action: () => {
                        playCoupon(false);
                    }
                },
                cancel: {
                    label: 'Vazgeç',
                    class: 'fw-bold'
                }
            }
        })
    }
}

/** Kupon panelindeki uzun vadeli seçim satırı. */
export function LongtermSelections() {
    const selection = LongtermBetslip.getSelection();
    if (!selection) return <></>;
    return <LongtermSelectionRow selection={selection}/>;
}

/** Kupon panelinin başlığında gösterilecek ad. */
export function getLongtermHeaderTitle(): string {
    return LongtermBetslip.getSelection()?.hippodrome.hippodrome || 'Uzun Vadeli';
}
